import { useEffect, useRef } from "react";

/**
 * Connects a WebSocket to /api/ws using the user's JWT token.
 * Calls onMessage(parsedData) on each message.
 * Auto-reconnects with backoff. Heartbeats every 25s.
 */
export function useRealtime(onMessage) {
  const wsRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    const token = localStorage.getItem("teslam_token");
    if (!token) return;

    let stopped = false;
    let retry = 0;
    let heartbeat = null;

    const open = () => {
      if (stopped) return;
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const wsUrl = base.replace(/^http/, "ws") + `/api/ws?token=${encodeURIComponent(token)}`;
      let ws;
      try {
        ws = new WebSocket(wsUrl);
      } catch (e) {
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        retry = 0;
        heartbeat = setInterval(() => {
          try { ws.send("ping"); } catch (e) { /* */ }
        }, 25000);
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          onMessageRef.current?.(msg);
        } catch (e) { /* */ }
      };

      ws.onclose = () => {
        if (heartbeat) clearInterval(heartbeat);
        heartbeat = null;
        if (stopped) return;
        retry += 1;
        const delay = Math.min(15000, 1000 * Math.pow(1.6, retry));
        setTimeout(open, delay);
      };

      ws.onerror = () => {
        try { ws.close(); } catch (e) { /* */ }
      };
    };

    open();

    return () => {
      stopped = true;
      if (heartbeat) clearInterval(heartbeat);
      try { wsRef.current?.close(); } catch (e) { /* */ }
    };
  }, []);
}
